// Analytics utility for WITAL.AI
// Enhanced tracking for user interactions and app usage

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

export interface UserPreference {
  language: string;
  city: string;
  countryCode?: string;
  textLength: number;
  fontSize: number;
  audience: string;
  models: string[];
  llm: string;
}

class AnalyticsService {
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private initialize() {
    // Check if gtag is available
    if (typeof window !== 'undefined' && window.gtag) {
      this.isInitialized = true;
      console.log('📊 Analytics initialized');
    } else {
      console.warn('⚠️ Google Analytics not loaded');
    }
  }

  /**
   * Track page views
   */
  trackPageView(pageTitle: string, pagePath?: string) {
    if (!this.isInitialized) return;

    window.gtag('config', 'GA_MEASUREMENT_ID', {
      page_title: pageTitle,
      page_location: pagePath || window.location.href,
      send_page_view: true
    });
  }

  /**
   * Track custom events
   */
  trackEvent(event: AnalyticsEvent) {
    if (!this.isInitialized) return;

    window.gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value
    });
  }

  /**
   * Track forecast requests
   */
  trackForecastRequest(city: string, language: string, preferences: UserPreference) {
    this.trackEvent({
      action: 'forecast_request',
      category: 'weather',
      label: `${city}_${language}`,
      value: preferences.textLength
    });

    // Track user preferences
    this.trackEvent({
      action: 'user_preferences',
      category: 'settings',
      label: `lang:${preferences.language}_audience:${preferences.audience}_models:${preferences.models.join(',')}`
    });
  }

  /**
   * Track language changes
   */
  trackLanguageChange(fromLanguage: string, toLanguage: string) {
    this.trackEvent({
      action: 'language_change',
      category: 'settings',
      label: `${fromLanguage}_to_${toLanguage}`
    });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(feature: string, details?: string) {
    this.trackEvent({
      action: 'feature_usage',
      category: 'app_interaction',
      label: feature
    });
  }

  /**
   * Track app installation
   */
  trackAppInstall() {
    this.trackEvent({
      action: 'app_install',
      category: 'pwa',
      label: 'WITAL.AI'
    });
  }

  /**
   * Track error events
   */
  trackError(errorType: string, errorMessage: string) {
    this.trackEvent({
      action: 'error',
      category: 'app_error',
      label: `${errorType}: ${errorMessage}`
    });
  }

  /**
   * Track user engagement
   */
  trackEngagement(action: string, duration?: number) {
    this.trackEvent({
      action: action,
      category: 'engagement',
      label: duration ? `${action}_${duration}s` : action,
      value: duration
    });
  }

  /**
   * Track API usage
   */
  trackApiUsage(endpoint: string, responseTime: number, success: boolean) {
    this.trackEvent({
      action: 'api_request',
      category: 'performance',
      label: `${endpoint}_${success ? 'success' : 'error'}`,
      value: responseTime
    });
  }
}

// Create singleton instance
export const analytics = new AnalyticsService();

// Export for use in components
export default analytics; 